import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useRealtime } from '../../lib/RealtimeContext.jsx';
import {
  Loading,
  Empty,
  StatusPill,
  Avatar,
  useToast,
  PageHeader,
  Segmented,
  Stars,
} from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { currency, formatDayLabel, timeRange, STATUS_LABEL, todayISO } from '../../lib/format.js';

const TABS = [
  ['pending', 'Requests'],
  ['accepted', 'Confirmed'],
  ['completed', 'Completed'],
  ['all', 'All'],
];

export default function NurseBookings() {
  const toast = useToast();
  const { unread } = useRealtime();
  const [bookings, setBookings] = useState(null);
  const [tab, setTab] = useState('pending');
  const [busy, setBusy] = useState(0);

  const load = () => api.get('/api/bookings').then(setBookings).catch(() => setBookings([]));
  useEffect(() => {
    load();
  }, []);

  if (!bookings) return <Loading rows={3} />;

  const respond = async (id, action) => {
    setBusy(id);
    try {
      const updated = await api.post(`/api/bookings/${id}/respond`, { action });
      setBookings((list) => list.map((b) => (b.id === id ? { ...b, ...updated } : b)));
      toast.success(action === 'accept' ? 'Booking accepted.' : 'Request declined.');
    } catch (err) {
      toast.error(err);
      load();
    } finally {
      setBusy(0);
    }
  };

  const complete = async (id) => {
    setBusy(id);
    try {
      const updated = await api.post(`/api/bookings/${id}/complete`);
      setBookings((list) => list.map((b) => (b.id === id ? { ...b, ...updated } : b)));
      toast.success('Visit marked completed. The patient can now leave a review.');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(0);
    }
  };

  const filtered = bookings.filter((b) => (tab === 'all' ? true : b.status === tab));
  const counts = bookings.reduce((acc, b) => ({ ...acc, [b.status]: (acc[b.status] || 0) + 1 }), {});

  return (
    <>
      <PageHeader title="Bookings" subtitle="Respond to requests and manage your confirmed visits." />

      <Segmented options={TABS} value={tab} onChange={setTab} counts={counts} className="mb-3" style={{ maxWidth: 520 }} />

      {filtered.length === 0 ? (
        <Empty art={tab === 'pending' ? 'inbox' : 'empty-bookings'} title={`No ${tab === 'all' ? '' : tab} bookings`}>
          {tab === 'pending'
            ? 'You have responded to everything. New requests will show up here.'
            : 'Nothing in this list yet.'}
        </Empty>
      ) : (
        <div className="stack">
          {filtered.map((b, i) => (
            <article key={b.id} className="card card-pad anim-rise" style={{ '--i': i }}>
              <div className="row" style={{ flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                <Avatar name={b.patient_name} avatar={b.patient_avatar} />
                <div className="grow">
                  <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                    <span className="t-title">{b.patient_name}</span>
                    <StatusPill status={b.status} label={STATUS_LABEL[b.status]} />
                    {b.payment_status === 'paid' && (
                      <span className="pill pill-success">
                        <Icon name="cash" /> Cash collected
                      </span>
                    )}
                  </div>
                  <div className="small muted" style={{ marginTop: 2 }}>
                    {b.service_name} · {formatDayLabel(b.date)} · {timeRange(b.start_time, b.end_time)} ({b.hours} hr)
                  </div>
                  <div className="tiny dim truncate row-tight" style={{ marginTop: 3 }}>
                    <Icon name="map-pin" size={13} />
                    {b.address}
                  </div>
                  {b.notes && (
                    <div className="tiny muted row-tight mt-1" style={{ alignItems: 'flex-start' }}>
                      <Icon name="file" size={13} style={{ marginTop: 2 }} />
                      <span>{b.notes}</span>
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="rate">{currency(b.amount)}</div>
                  <div className="rate-unit">cash</div>
                </div>
              </div>

              <div className="row mt-3">
                <Link to={`/nurse/bookings/${b.id}`} className="btn btn-sm btn-ghost">
                  Details
                </Link>
                {unread.byBooking[b.id] > 0 && (
                  <Link to={`/nurse/bookings/${b.id}`} className="btn btn-sm">
                    <Icon name="message" /> {unread.byBooking[b.id]} new
                  </Link>
                )}
                <div className="spacer" />
                {b.status === 'pending' && (
                  <>
                    <button className="btn btn-sm" disabled={busy === b.id} onClick={() => respond(b.id, 'decline')}>
                      Decline
                    </button>
                    <button className="btn btn-sm btn-success" disabled={busy === b.id} onClick={() => respond(b.id, 'accept')}>
                      {busy === b.id ? (
                        <span className="spinner" />
                      ) : (
                        <>
                          <Icon name="check" /> Accept
                        </>
                      )}
                    </button>
                  </>
                )}
                {b.status === 'accepted' && b.date <= todayISO() && (
                  <button className="btn btn-sm btn-primary" disabled={busy === b.id} onClick={() => complete(b.id)}>
                    {busy === b.id ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <Icon name="flag" /> Mark completed
                      </>
                    )}
                  </button>
                )}
                {b.status === 'accepted' && b.date > todayISO() && (
                  <span className="tiny dim">Starts {formatDayLabel(b.date)}</span>
                )}
                {b.status === 'completed' && b.review_rating && <Stars value={b.review_rating} />}
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
